import React, { useCallback, useEffect } from 'react';
import { Panel } from 'reactflow';
import { useReplay } from '../../../contexts/ReplayContext';

const isTyping = (el: EventTarget | null) => {
  const n = el as HTMLElement | null; if (!n) return false;
  const t = n.tagName; return t === 'INPUT' || t === 'TEXTAREA' || n.isContentEditable === true;
};

export function ReplayTransport({ onExit, bufferedCount }: { onExit: () => void; bufferedCount: number }) {
  const { active, stepIndex, playing, speed, steps, setStepIndex, togglePlay, setSpeed } = useReplay();
  const last = steps.length - 1;
  const atEnd = stepIndex >= last;

  const onPlay = useCallback(() => { if (atEnd) setStepIndex(0); else togglePlay(); }, [atEnd, setStepIndex, togglePlay]);

  useEffect(() => {   // keyboard: space play/pause, ←/→ step, Escape exit — guarded
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === ' ') { e.preventDefault(); onPlay(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); setStepIndex(stepIndex + 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); setStepIndex(stepIndex - 1); }
      else if (e.key === 'Escape') { e.preventDefault(); onExit(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, stepIndex, onPlay, setStepIndex, onExit]);

  if (!active) return null;
  const btn = 'px-2 py-1 text-sm leading-none rounded text-amber-700 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40';
  const speeds: (1 | 2 | 4)[] = [1, 2, 4];
  return (
    <Panel position="bottom-center">
      <div data-testid="replay-transport"
           className="flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg
                      bg-white/95 dark:bg-gray-900/95 border border-amber-200 dark:border-amber-800/60">
        <button type="button" data-testid="replay-play" onClick={onPlay} className={btn}
                title={atEnd ? 'Restart (space)' : playing ? 'Pause (space)' : 'Play (space)'}>
          {atEnd ? '↻' : playing ? '❚❚' : '▶'}
        </button>
        <button type="button" data-testid="replay-prev" onClick={() => setStepIndex(stepIndex - 1)} className={btn} title="Previous (←)">⏮</button>
        <button type="button" data-testid="replay-next" onClick={() => setStepIndex(stepIndex + 1)} className={btn} title="Next (→)">⏭</button>
        <input data-testid="replay-scrubber" type="range" min={0} max={Math.max(0, last)} value={stepIndex}
               onChange={(e) => setStepIndex(Number(e.target.value))}
               className="w-40 accent-amber-500" aria-label="Replay position" />
        <span data-testid="replay-counter" className="text-xs tabular-nums text-gray-700 dark:text-gray-200">
          Exchange {stepIndex + 1} / {steps.length}
        </span>
        <span className="flex items-center gap-0.5">
          {speeds.map((s) => (
            <button key={s} type="button" data-testid={`replay-speed-${s}`} onClick={() => setSpeed(s)}
                    className={`${btn} ${speed === s ? 'bg-amber-200 dark:bg-amber-800/60' : ''}`}
                    title={`${s}× speed`}>{s}×</button>
          ))}
        </span>
        {bufferedCount > 0 && (
          <span data-testid="replay-live-paused" className="text-[10px] text-amber-600 dark:text-amber-300">
            live paused • {bufferedCount} new
          </span>
        )}
        <button type="button" data-testid="replay-exit" onClick={onExit} className={btn} title="Exit replay (Esc)">✕</button>
      </div>
    </Panel>
  );
}
